import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, X } from 'lucide-react';

interface DropdownOption {
  value: string;
  label: string;
  subtitle?: string;
}

interface FilteredInputDropdownProps {
  options: DropdownOption[];
  selectedValue?: string;
  onSelect: (value: string) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  isLoading?: boolean;
  noResultsText?: string;
  inputClassName?: string;
}

const FilteredInputDropdown: React.FC<FilteredInputDropdownProps> = ({
  options,
  selectedValue,
  onSelect,
  placeholder = 'Search and select...',
  className = '',
  disabled = false,
  isLoading = false,
  noResultsText = 'No results found',
  inputClassName = '',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [displayValue, setDisplayValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Update display value when selectedValue changes
  useEffect(() => {
    if (selectedValue) {
      const selectedOption = options.find(option => option.value === selectedValue);
      setDisplayValue(selectedOption?.label || '');
      setSearchValue(selectedOption?.label || '');
    } else {
      setDisplayValue('');
      setSearchValue('');
    }
  }, [selectedValue, options]);

  // Filter options based on search value
  const filteredOptions = options.filter(option =>
    option && option.label &&option.label.toLowerCase().includes(searchValue.toLowerCase()) ||
    (option.subtitle && option.subtitle.toLowerCase().includes(searchValue.toLowerCase()))
  );

  // Handle input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchValue(value);
    setDisplayValue(value);
    setIsOpen(true);
  };

  // Handle option selection
  const handleOptionSelect = (option: DropdownOption) => {
    setSearchValue(option.label);
    setDisplayValue(option.label);
    setIsOpen(false);
    onSelect(option.value);
    inputRef.current?.blur();
  };

  // Handle input focus
  const handleInputFocus = () => {
    if (!disabled && !isLoading) {
      setIsOpen(true);
    }
  };

  // Handle clear selection
  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    setSearchValue('');
    setDisplayValue('');
    onSelect('');
    inputRef.current?.focus();
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        // Reset display value if no valid selection
        if (selectedValue) {
          const selectedOption = options.find(option => option.value === selectedValue);
          setDisplayValue(selectedOption?.label || '');
          setSearchValue(selectedOption?.label || '');
        } else {
          setDisplayValue('');
          setSearchValue('');
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [selectedValue, options]);

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
      inputRef.current?.blur();
    } else if (e.key === 'ArrowDown' && !isOpen) {
      setIsOpen(true);
    }
  };

  if (isLoading) {
    return (
      <div className={className}>
        <div className="relative">
          <input
            type="text"
            className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-500 cursor-not-allowed focus:outline-none"
            value="Loading..."
            disabled
            readOnly
          />
          <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
        </div>
      </div>
    );
  }

  return (
    <div className={`relative ${className}`}>
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          className={`w-full px-3 py-1 md:text-sm rounded-xl placeholder:text-black/75 h-10 focus:outline-none focus:ring-0 focus:border-gray-300 focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-transparent ${inputClassName} ${
            disabled ? 'cursor-not-allowed' : ''
          } ${displayValue ? 'pr-16' : 'pr-10'}`}
          value={displayValue}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          autoComplete="off"
        />

        <div className="absolute right-2 top-1/2 transform -translate-y-1/2 flex items-center gap-1">
          {displayValue && !disabled && (
            <button
              type="button"
              onClick={handleClear}
              className="p-1 hover:bg-gray-100 rounded-full transition-colors"
              tabIndex={-1}
            >
              <X className="h-3 w-3 text-gray-400 hover:text-gray-600" />
            </button>
          )}
          <ChevronDown
            className={`h-4 w-4 text-gray-400 transition-transform ${
              isOpen ? 'rotate-180' : ''
            }`}
          />
        </div>
      </div>

      {isOpen && (
        <div
          ref={dropdownRef}
          className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto"
        >
          {filteredOptions.length === 0 ? (
            <div className="px-3 py-2 text-gray-500 text-sm">
              {noResultsText}
            </div>
          ) : (
            filteredOptions.map((option) => (
              <div
                key={option.value}
                className="px-3 py-2 hover:bg-gray-100 cursor-pointer transition-colors"
                onClick={() => handleOptionSelect(option)}
              >
                <div className="font-medium text-gray-900">
                  {option.label}
                </div>
                {option.subtitle && (
                  <div className="text-xs text-gray-500 mt-1">
                    {option.subtitle}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default FilteredInputDropdown;